import { Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = sessionStorage.getItem('token');

        if (!token) {
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        const API_URL = import.meta.env.VITE_API_URL || '/api';

        // Make API call with authorization header
        const response = await fetch(`${API_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.error('Auth check failed:', response.status);
          
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setError(`Authentication failed: ${response.statusText}`);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        const data = await response.json();

        setIsAuthenticated(true);
        setIsAdmin(data.role === 'admin');

        sessionStorage.setItem('user', JSON.stringify(data));

        // For admin routes, show notification if missing admin privileges
        if (requireAdmin && data.role !== 'admin') {
          toast.error('This page requires admin privileges');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        
        setError(`Error: ${error.message}`);
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [requireAdmin]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <div className="mt-4 text-gray-600">Verifying authentication...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Show error message if there was an error
    if (error) {
      toast.error(error);
    }
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    toast.error('You need admin privileges to access this page');
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute; 
